import {
  CareerCopilotClient,
  ChatFeedbackRequest,
  ChatMessage,
  CopilotRequest,
  CopilotResponse,
  FeedbackClient,
  ReferenceType,
} from '../models/Copilot.types';
import { IConfig, ResourceInfo } from '../models/IConfig';
import { BaseService } from './BaseService';
import { IPublicClientApplication } from '@azure/msal-browser';

export class CopilotService extends BaseService {
  private readonly careerChatClient: CareerCopilotClient;
  private readonly feedbackClient: FeedbackClient;
  private readonly usageUserId: Promise<string>;
  private readonly flightData: any;
  public constructor(
    resourceInfo: ResourceInfo,
    msalInstance: IPublicClientApplication,
    usageUserId: Promise<string>,
    flightData: any
  ) {
    super(resourceInfo, msalInstance);
    //TODO: Need to figure out a way not to have .bind in each service.
    const getAccessToken = this.getAccessToken.bind(this);
    const config: IConfig = {
      getAuthorization: getAccessToken,
      AdditionalHeaders: {
        'Ocp-APIM-Subscription-Key': __HR_APIM_SUBSCRIPTION_KEY__,
      },
    };

    this.careerChatClient = new CareerCopilotClient(config, resourceInfo.resourceBaseUrl);
    this.feedbackClient = new FeedbackClient(config, resourceInfo.resourceBaseUrl);
    this.usageUserId = usageUserId;
    this.flightData = flightData;
  }

  private debugResponse = (latest: ChatMessage): CopilotResponse => {
    const help = 'help';
    const experts = 'experts';
    const insights = 'insights';
    const links = 'links';
    const profile = 'profile';
    const variety = 'variety';
    const options = [help, experts, insights, links, profile, variety];
    const followupQuestions = options.map((o) => `__DEBUG-${o}`).join(';');
    switch (latest.messageText) {
      case `__DEBUG-${help}`:
        return {
          userRequest: undefined,
          reply: 'Debug prompts use hardcoded values for testing. Try one of the debug prompts below.',
          references: [],
          followupQuestions,
          copilotMessageId: latest.messageId,
          createdTimestamp: latest.createdTimestamp,
        };
      // return expert references
      case `__DEBUG-${experts}`:
        return {
          reply: 'Here are some experts in various skills related to your career development plan.',
          userRequest: undefined,
          references: [
            {
              type: ReferenceType.Opportunity,
              roleLibraryReferences: {},
            },
          ],
          followupQuestions,
          copilotMessageId: latest.messageId,
          createdTimestamp: latest.createdTimestamp,
        };
      // return an insights reference
      case `__DEBUG-${insights}`:
        return {
          reply: 'Here are the top 5 skills in your org',
          userRequest: undefined,
          references: [
            {
              type: ReferenceType.Insights,
              roleLibraryReferences: undefined,
            },
          ],
          followupQuestions,
          copilotMessageId: latest.messageId,
          createdTimestamp: latest.createdTimestamp,
        };
      // return link references
      // case `__DEBUG-${links}`:
      //   return {
      //     reply:
      //       'Here is a sample Role Library response:\nBased on the provided information, the skills needed to succeed in the role of Quantum Software Engineering IC2 are:\n\n1. English Language Proficiency: The ability to speak and understand English [0]\n2. Software Coding: Knowledge of and ability to develop code [1]\n3. Code Comprehension: The ability to understand and extract relevant information from code [2]',
      //     references: [
      //       { type: 'link', data: 'https://roleLibrary-client-uat.azureedge.net/role/Role_13010063' },
      //       { type: 'link', data: 'https://roleLibrary-client-uat.azureedge.net/role/Role_13010064' },
      //       { type: 'link', data: 'https://roleLibrary-client-uat.azureedge.net/role/Role_13010065' },
      //     ],
      //     followupQuestions,
      //   };
      case `__DEBUG-${profile}`: {
        return {
          reply:
            'Based on the provided information, your skills include: \n1. C#\n2. Writes Code\n\n[https://roleLibrary-client-uat.azureedge.net/profile]',
          userRequest: undefined,
          references: [],
          followupQuestions,
          copilotMessageId: latest.messageId,
          createdTimestamp: latest.createdTimestamp,
        };
      }
      // both expert and non-expert reference cards
      case `__DEBUG-${variety}`:
        return {
          reply: 'Here are some experts and learnings to advance your career.',
          userRequest: undefined,
          references: [
            {
              type: ReferenceType.Opportunity,
              roleLibraryReferences: undefined,
            },
            {
              type: ReferenceType.Opportunity,
              roleLibraryReferences: undefined,
            },
          ],
          followupQuestions,
          copilotMessageId: latest.messageId,
          createdTimestamp: latest.createdTimestamp,
        };
      default:
        return {
          reply: 'Unrecognized debug prompt.',
          references: [],
          userRequest: undefined,
          followupQuestions,
          copilotMessageId: latest.messageId,
          createdTimestamp: latest.createdTimestamp,
        };
    }
  };

  public async chat(request: CopilotRequest): Promise<CopilotResponse> {
    const prompts = request.conversationHistory ?? [];
    const latest = prompts[prompts.length - 1];
    if (latest.messageText.startsWith('__DEBUG')) return Promise.resolve(this.debugResponse(latest));
    return await this.careerChatClient.chat(request);
  }

  public async saveChatFeedback(ChatFeedbackRequest: ChatFeedbackRequest): Promise<void> {
    if (ChatFeedbackRequest.usageUserId == '' || !ChatFeedbackRequest.usageUserId) {
      ChatFeedbackRequest.usageUserId = await this.usageUserId;
    }
    if (ChatFeedbackRequest.flightingInformation == '' || !ChatFeedbackRequest.flightingInformation) {
      ChatFeedbackRequest.flightingInformation = JSON.stringify(this.flightData);
    }
    return await this.feedbackClient.saveChatFeedback(ChatFeedbackRequest);
  }
}
